// W3C traceparent on every frame, so a relay trace and a client error report share one
// trace id. Only the trace id is meaningful: the browser exports no spans. The relay
// reads it generically off the inner frame (frame-span.ts); high-frequency or
// handshake frames stay untraced to keep span volume down.
const UNTRACED_FRAME_TYPES: ReadonlySet<string> = new Set([
  "cyborg:ping",
  "cyborg:typing",
  "cyborg:terminal_input",
  "cyborg:auth",
]);

export function isTracedFrame(type: unknown): boolean {
  return typeof type === "string" && !UNTRACED_FRAME_TYPES.has(type);
}

function randomHex(bytes: number): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
}

// All-zero ids are invalid per the spec; 16 random bytes make one vanishingly rare.
export function newTraceparent(): string {
  return `00-${randomHex(16)}-${randomHex(8)}-01`;
}

export function traceIdOf(traceparent: string | undefined): string | undefined {
  return traceparent?.split("-")[1];
}

/** The frame with a traceparent when its type is traced and it carries none yet. */
export function withTraceparent(data: Record<string, unknown>): Record<string, unknown> {
  if (!isTracedFrame(data.type) || typeof data.traceparent === "string") return data;
  return { ...data, traceparent: newTraceparent() };
}

/** Tag an error rejected for a traced request, so its report carries `trace_id`. */
export function withTraceId<E>(err: E, traceId: string | undefined): E {
  if (traceId && err instanceof Error) Object.assign(err, { traceId });
  return err;
}
